import { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatDisplayDate } from "@/lib/dateFormat";
import AdminLayout from "@/components/AdminLayout";
import FilterBar from "@/components/FilterBar";
import { SortableTH, useSortableData, EmptyState } from "@/components/SortableTable";
import ColumnFilterRow, { applyColumnFilters, columnFiltersActive } from "@/components/ColumnFilterRow";
import { useCompanyFilter } from "@/lib/companyFilter";
import { FileSpreadsheet } from "lucide-react";

const downloadBlob = async (url, filename, params = {}) => {
  const res = await api.get(url, { params, responseType: "blob" });
  const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(blobUrl);
};

const COL_TYPES = {
  code: "text",
  name: "text",
  department: "select",
  designation: "text",
  employment_type: "select",
  status: "select",
  joining_date: "date",
  bank_name: "text",
  account_number: "text",
  ifsc: "text",
  pan: "text",
  uan: "text",
};

/**
 * Bank Details report — for payroll to register new joiners' bank accounts.
 */
export default function BankDetailsReport() {
  const { companyFilter, empParams, companyId } = useCompanyFilter();
  const [emps, setEmps] = useState([]);
  const [deps, setDeps] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("Active");
  const [departmentId, setDepartmentId] = useState("");
  const [empType, setEmpType] = useState("");
  const [joiningFrom, setJoiningFrom] = useState("");
  const [joiningTo, setJoiningTo] = useState("");
  const [missingBankOnly, setMissingBankOnly] = useState(false);
  const [colFilters, setColFilters] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/employees", { params: empParams }),
      api.get("/departments"),
      api.get("/designations"),
    ]).then(([e, d, des]) => {
      setEmps(e.data || []);
      setDeps(d.data || []);
      setDesigs(des.data || []);
    });
  }, [companyId, empParams]);

  const depMap = useMemo(() => Object.fromEntries(deps.map((d) => [d.id, d.name])), [deps]);
  const desigMap = useMemo(() => Object.fromEntries(desigs.map((d) => [d.id, d.title])), [desigs]);

  const rows = useMemo(() => {
    return emps
      .filter((e) => {
        const emp = e.employment || {};
        const sal = e.salary || {};
        if (status && emp.status !== status) return false;
        if (departmentId && emp.department_id !== departmentId) return false;
        if (empType && emp.employment_type !== empType) return false;
        const jd = emp.joining_date || "";
        if (joiningFrom && jd < joiningFrom) return false;
        if (joiningTo && jd > joiningTo) return false;
        if (missingBankOnly) {
          const incomplete =
            !(sal.bank_name || "").trim() ||
            !(sal.account_number || "").trim() ||
            !(sal.ifsc || "").trim();
          if (!incomplete) return false;
        }
        if (search) {
          const s = search.toLowerCase();
          const name = [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!name.includes(s) && !(e.basic?.code || "").toLowerCase().includes(s)) return false;
        }
        return true;
      })
      .map((e) => {
        const emp = e.employment || {};
        const sal = e.salary || {};
        return {
          id: e.id,
          code: e.basic?.code || "",
          name: [e.basic?.first_name, e.basic?.middle_name, e.basic?.last_name].filter(Boolean).join(" "),
          department: depMap[emp.department_id] || "",
          designation: desigMap[emp.designation_id] || "",
          employment_type: emp.employment_type || "",
          status: emp.status || "",
          joining_date: emp.joining_date || "",
          bank_name: sal.bank_name || "",
          account_number: sal.account_number || "",
          ifsc: sal.ifsc || "",
          pan: sal.pan || "",
          uan: sal.uan || "",
        };
      });
  }, [emps, status, departmentId, empType, joiningFrom, joiningTo, missingBankOnly, search, depMap, desigMap]);

  const getVal = useCallback((r, key) => r[key], []);
  const filtered = useMemo(
    () => applyColumnFilters(rows, colFilters, getVal, COL_TYPES),
    [rows, colFilters, getVal]
  );
  const { items, sortBy, sortDir, requestSort } = useSortableData(filtered, { initialKey: "joining_date" });

  const reset = () => {
    setSearch("");
    setStatus("Active");
    setDepartmentId("");
    setEmpType("");
    setJoiningFrom("");
    setJoiningTo("");
    setMissingBankOnly(false);
    setColFilters({});
  };

  const exportExcel = async () => {
    setBusy(true);
    try {
      const params = {};
      if (status) params.status_filter = status;
      if (search) params.search = search;
      if (joiningFrom) params.joining_from = joiningFrom;
      if (joiningTo) params.joining_to = joiningTo;
      if (departmentId) params.department_id = departmentId;
      if (empType) params.employment_type = empType;
      if (missingBankOnly) params.missing_bank_only = true;
      const stamp = new Date().toISOString().slice(0, 10);
      await downloadBlob("/exports/bank-details.xlsx", `bank_details_${stamp}.xlsx`, params);
    } catch {
      alert("Export failed");
    } finally {
      setBusy(false);
    }
  };

  const incompleteCount = items.filter(
    (r) => !(r.bank_name || "").trim() || !(r.account_number || "").trim() || !(r.ifsc || "").trim()
  ).length;

  return (
    <AdminLayout title="Bank Details Report" breadcrumbs={["Home", "Reports", "Bank Details"]}>
      <div className="space-y-4">
        <p className="text-sm text-slate-600">
          Pull bank account details for payroll (e.g. new joiners). Filter by joining date, then download Excel for the bank / payroll team.
        </p>
        <FilterBar
          filters={[
            companyFilter,
            { type: "search", key: "q", label: "Search", value: search, onChange: setSearch },
            {
              type: "select",
              key: "status",
              label: "Status",
              value: status,
              onChange: setStatus,
              options: [
                { value: "", label: "All statuses" },
                { value: "Active", label: "Active" },
                { value: "Onboarding", label: "Onboarding" },
                { value: "Probation", label: "Probation" },
                { value: "Notice Period", label: "Notice Period" },
                { value: "Inactive", label: "Inactive" },
              ],
            },
            {
              type: "select",
              key: "dept",
              label: "Department",
              value: departmentId,
              onChange: setDepartmentId,
              options: [
                { value: "", label: "All departments" },
                ...deps.map((d) => ({ value: d.id, label: d.name })),
              ],
            },
            {
              type: "select",
              key: "type",
              label: "Employment Type",
              value: empType,
              onChange: setEmpType,
              options: [
                { value: "", label: "All types" },
                { value: "Full-time", label: "Full-time" },
                { value: "Part-time", label: "Part-time" },
                { value: "Contract", label: "Contract" },
                { value: "Intern", label: "Intern" },
              ],
            },
            { type: "date", key: "from", label: "Joined from", value: joiningFrom, onChange: setJoiningFrom },
            { type: "date", key: "to", label: "Joined to", value: joiningTo, onChange: setJoiningTo },
          ]}
          onReset={reset}
          rightSlot={
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={missingBankOnly}
                  onChange={(e) => setMissingBankOnly(e.target.checked)}
                  data-testid="bank-missing-only"
                />
                Missing bank only
              </label>
              <button
                type="button"
                disabled={busy}
                onClick={exportExcel}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-60"
                data-testid="bank-export-xlsx"
              >
                <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
                {busy ? "Exporting…" : "Excel"}
              </button>
            </div>
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-white border border-slate-200 rounded p-3">
            <div className="text-xs text-slate-500">Employees shown</div>
            <div className="text-xl font-semibold text-slate-800">{items.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded p-3">
            <div className="text-xs text-slate-500">Incomplete bank details</div>
            <div className={`text-xl font-semibold ${incompleteCount ? "text-amber-600" : "text-emerald-600"}`}>
              {incompleteCount}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-slate-200 overflow-x-auto" data-testid="bank-details-table">
          <div className="px-5 py-2 text-xs border-b border-slate-200 flex gap-3">
            {columnFiltersActive(colFilters) && (
              <button type="button" onClick={() => setColFilters({})} className="text-[#226DB4] hover:underline">
                Clear column filters
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                {[
                  ["code", "Code"],
                  ["name", "Name"],
                  ["department", "Department"],
                  ["designation", "Designation"],
                  ["employment_type", "Type"],
                  ["status", "Status"],
                  ["joining_date", "Joining"],
                  ["bank_name", "Bank"],
                  ["account_number", "Account No"],
                  ["ifsc", "IFSC"],
                  ["pan", "PAN"],
                  ["uan", "UAN"],
                ].map(([k, label]) => (
                  <SortableTH key={k} columnKey={k} active={sortBy} dir={sortDir} onClick={requestSort}>
                    {label}
                  </SortableTH>
                ))}
              </tr>
              <ColumnFilterRow
                columns={Object.keys(COL_TYPES).map((key) => ({
                  key,
                  filterType: COL_TYPES[key],
                  options:
                    key === "status"
                      ? ["Active", "Onboarding", "Probation", "Notice Period", "Inactive"]
                      : key === "employment_type"
                        ? ["Full-time", "Part-time", "Contract", "Intern"]
                        : key === "department"
                          ? deps.map((d) => d.name)
                          : undefined,
                }))}
                filters={colFilters}
                onChange={(key, value) => setColFilters((prev) => ({ ...prev, [key]: value }))}
              />
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.length === 0 ? (
                <EmptyState message="No employees match filters" colSpan={12} />
              ) : (
                items.map((r) => {
                  const incomplete =
                    !(r.bank_name || "").trim() ||
                    !(r.account_number || "").trim() ||
                    !(r.ifsc || "").trim();
                  return (
                    <tr key={r.id} className={incomplete ? "bg-amber-50/40" : undefined}>
                      <td className="px-4 py-2 font-medium text-slate-800">{r.code}</td>
                      <td className="px-4 py-2 text-slate-700">{r.name}</td>
                      <td className="px-4 py-2 text-slate-600">{r.department || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{r.designation || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{r.employment_type || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{r.status || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{formatDisplayDate(r.joining_date)}</td>
                      <td className="px-4 py-2 text-slate-700">{r.bank_name || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.account_number || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.ifsc || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{r.pan || "—"}</td>
                      <td className="px-4 py-2 text-slate-600">{r.uan || "—"}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
}
